import { useState, type FormEvent } from 'react'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { AvisoError, CabeceraModal, CabeceraPagina, Campo, CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_INPUT, Insignia } from '../componentes/formulario'
import { IconoAbrir, IconoCorrecto } from '../componentes/iconos'
import { Modal } from '../componentes/Modal'
import { SkeletonList } from '../componentes/Skeleton'
import type { AvisoManual, EtiquetaSemana, RespuestaAviso } from '../api/modelos'
import { useAvisosManuales, useMarcarEnviado, useRegistrarRespuesta } from '../hooks/useAvisosManuales'
import { useIntegraciones } from '../hooks/useIntegraciones'
import { SEMANAS } from './agendamiento/tabla'
import { BarraPaginacion } from '../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../componentes/paginacion'

const RESPUESTAS: { valor: RespuestaAviso; etiqueta: string }[] = [
  { valor: 'si_habitual', etiqueta: 'Sí, el pedido habitual' },
  { valor: 'si_cambios', etiqueta: 'Sí, con cambios' },
  { valor: 'no_semana', etiqueta: 'No esta semana' },
  { valor: 'baja', etiqueta: 'Baja' },
]

const ESTADOS: Record<AvisoManual['estado'], { texto: string; tono: 'neutro' | 'atencion' | 'positivo' }> = {
  pendiente: { texto: 'Pendiente', tono: 'atencion' },
  enviado: { texto: 'Enviado', tono: 'neutro' },
  respondido: { texto: 'Respondido', tono: 'positivo' },
}

function ModalRespuesta({ aviso, semana, onCerrar }: { aviso: AvisoManual; semana: EtiquetaSemana | ''; onCerrar: () => void }) {
  const registrar = useRegistrarRespuesta()
  const [respuesta, setRespuesta] = useState<RespuestaAviso | ''>('')
  const [nota, setNota] = useState('')
  const [fecha, setFecha] = useState('')
  const titulo = `Respuesta de ${aviso.nombre}`

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    if (!respuesta) return
    registrar.mutate(
      { contactId: aviso.contactId, respuesta, nota: nota.trim() || undefined, fecha: fecha || undefined, semana },
      { onSuccess: onCerrar },
    )
  }

  return (
    <Modal titulo={titulo} onCerrar={onCerrar}>
      <CabeceraModal titulo={titulo} onCerrar={onCerrar} />
      <form onSubmit={enviar} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-tinta">¿Qué ha contestado?</legend>
          {RESPUESTAS.map((r) => (
            <label key={r.valor} className="flex items-center gap-2 text-sm text-tinta-2">
              <input type="radio" name="respuesta" value={r.valor} checked={respuesta === r.valor} onChange={() => setRespuesta(r.valor)} />
              {r.etiqueta}
            </label>
          ))}
        </fieldset>
        {respuesta === 'si_habitual' ? (
          <Campo etiqueta="Fecha del reparto (opcional)" ayuda="Si la pones, se crea el albarán de ese día, como con el «Sí» del bot.">
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={CLASE_INPUT} />
          </Campo>
        ) : null}
        <Campo etiqueta="Nota (opcional)" ayuda={respuesta === 'si_cambios' ? 'Apunta los cambios que pide.' : undefined}>
          <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} className={CLASE_INPUT} />
        </Campo>
        <AvisoError error={registrar.error} />
        <div className="flex gap-2">
          <button type="submit" disabled={!respuesta || registrar.isPending} className={CLASE_BOTON_PRIMARIO}>
            {registrar.isPending ? 'Guardando' : 'Registrar respuesta'}
          </button>
          <button type="button" onClick={onCerrar} className={CLASE_BOTON}>Cancelar</button>
        </div>
      </form>
    </Modal>
  )
}

function Tarjeta({ aviso, semana, onResponder }: { aviso: AvisoManual; semana: EtiquetaSemana | ''; onResponder: () => void }) {
  const enviado = useMarcarEnviado()
  const estado = ESTADOS[aviso.estado]
  const respuesta = RESPUESTAS.find((r) => r.valor === aviso.respuesta)?.etiqueta
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-borde bg-superficie p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-tinta">{aviso.nombre}</p>
          <p className="text-xs tabular-nums text-tinta-apagada">{aviso.movil ?? 'Sin móvil'}</p>
        </div>
        <Insignia tono={estado.tono}>{estado.texto}</Insignia>
      </div>
      <p className="whitespace-pre-line rounded-lg bg-elevado px-3 py-2 text-xs leading-relaxed text-tinta-2">{aviso.texto}</p>
      {respuesta ? <p className="text-xs text-tinta-2">Respuesta: <strong>{respuesta}</strong></p> : null}
      <div className="flex flex-wrap gap-2">
        {aviso.waUrl ? (
          <a href={aviso.waUrl} target="_blank" rel="noopener noreferrer" className={CLASE_BOTON} aria-label={`Abrir WhatsApp con ${aviso.nombre}`}>
            <IconoAbrir aria-hidden="true" className="size-4" />
            Abrir WhatsApp
          </a>
        ) : (
          <span className="self-center text-xs text-atencion-tinta">Sin móvil: avísale por otra vía.</span>
        )}
        {aviso.estado === 'pendiente' ? (
          <button
            type="button"
            className={CLASE_BOTON}
            disabled={enviado.isPending}
            onClick={() => enviado.mutate({ contactId: aviso.contactId, semana })}
            aria-label={`Marcar enviado a ${aviso.nombre}`}
          >
            <IconoCorrecto aria-hidden="true" className="size-4" />
            Marcar enviado
          </button>
        ) : null}
        <button type="button" className={CLASE_BOTON} onClick={onResponder} aria-label={`Registrar respuesta de ${aviso.nombre}`}>
          Registrar respuesta
        </button>
      </div>
      <AvisoError error={enviado.error} />
    </li>
  )
}

/**
 * Aviso semanal en modo manual (RF-45). El panel redacta el texto y el enlace
 * wa.me; el operador lo envía desde su WhatsApp, lo marca como enviado y apunta
 * lo que contesta el cliente. Nada sale a la red desde el servidor (RF-02).
 */
export function AvisoSemanal() {
  const [semana, setSemana] = useState<EtiquetaSemana | ''>('')
  const avisos = useAvisosManuales(semana)
  const integraciones = useIntegraciones()
  const [respondiendo, setRespondiendo] = useState<AvisoManual | null>(null)
  const modo = integraciones.data?.whatsapp?.modo
  const lista = avisos.data ?? []
  const pendientes = lista.filter((a) => a.estado === 'pendiente').length
  const pag = usePaginacionLocal(lista, semana)

  return (
    <div className="flex flex-col gap-4">
      <CabeceraPagina
        titulo="Aviso semanal"
        descripcion="Avisa a cada cliente desde tu WhatsApp con el texto ya redactado, márcalo como enviado y registra lo que conteste. Cuenta igual que una respuesta del bot en el corte del viernes."
      />
      {modo && modo !== 'manual' ? (
        <p role="status" className="rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm text-atencion-tinta">
          El canal de WhatsApp está en modo «{modo}»: los avisos los envía la integración. Puedes seguir registrando aquí las respuestas que te lleguen por otra vía.
        </p>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Campo etiqueta="Semana del ciclo">
            <select value={semana} onChange={(e) => setSemana(e.target.value as EtiquetaSemana | '')} className={CLASE_INPUT}>
              <option value="">La semana actual</option>
              {SEMANAS.map((s) => <option key={s.clave} value={s.clave}>{s.etiqueta}</option>)}
            </select>
          </Campo>
        </div>
        {avisos.isSuccess ? (
          <p className="pb-2 text-sm text-tinta-apagada">
            {lista.length} clientes · {pendientes} sin avisar
          </p>
        ) : null}
      </div>

      {avisos.isPending ? (
        <SkeletonList />
      ) : avisos.isError ? (
        <EstadoError error={avisos.error} onReintentar={() => void avisos.refetch()} />
      ) : lista.length === 0 ? (
        <EstadoVacio titulo="Nadie a quien avisar esta semana" descripcion="No hay clientes activos en la semana elegida." />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {pag.visibles.map((a) => <Tarjeta key={a.contactId} aviso={a} semana={semana} onResponder={() => setRespondiendo(a)} />)}
        </ul>
      )}

      <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} />

      {respondiendo ? <ModalRespuesta aviso={respondiendo} semana={semana} onCerrar={() => setRespondiendo(null)} /> : null}
    </div>
  )
}
